import { Box, IconButton, keyframes, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useGlobalMessage } from "../context/globalMessage";
import { useTranslation } from "react-i18next";
import useGetGlobalMessage from "../data-access/globalMessage/useGetGlobalMessage";
import { useAuth } from "../context/useAuth";
import useGetChamberInfoMessage from "../data-access/globalMessage/useGetChamberInfoMessage";
import CloseIcon from "@mui/icons-material/Close";

export default function GlobalMessage({
  children,
}: {
  children?: React.ReactNode;
}) {
  const {
    maintenance,
    infoMessage,
    setInfoMessage,
    chamberMessage,
    setChamberMessage,
  } = useGlobalMessage();
  const { t } = useTranslation();

  const [maintenanceChanged, setMaintenanceChanged] = useState<{
    changed: boolean;
    message: string;
  }>({
    changed: true,
    message: "",
  });
  const { isChamber } = useAuth();
  const [info, setInfo] = useState("");
  const [chamberInfo, setChamberInfo] = useState("");
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [showChamberInfo, setShowChamberInfo] = useState<boolean>(false);
  const [dismissedMaintenance, setDismissedMaintenance] = useState(false);
  const [dismissedInfo, setDismissedInfo] = useState(false);
  const [dismissedChamberInfo, setDismissedChamberInfo] = useState(false);

  const handleDismissMaintenance = () => {
    setDismissedMaintenance(true);
    setMaintenanceChanged((prev) => ({ ...prev, changed: false }));
  };

  const handleDismissInfo = () => {
    setDismissedInfo(true);
    setShowInfo(false);
  };

  const handleDismissChamberInfo = () => {
    setDismissedChamberInfo(true);
    setShowChamberInfo(false);
  };

  const { isLoading: isLoadingGlobalMessage, data: globalMessageData } =
    useGetGlobalMessage();

  const { isLoading: isLoadingChamberGlobalMessage, data: chamberMessageData } =
    useGetChamberInfoMessage(isChamber);

  const slideInOutAnimation = keyframes`
		0% {
			transform: translateY(-100%);
		}
		10%, 90% {
			transform: translateY(0);
		}
		100% {
			transform: translateY(-150%);
			display: none
		}
	`;

  useEffect(() => {
    setDismissedInfo(false);
  }, [infoMessage.message]);

  useEffect(() => {
    setDismissedMaintenance(false);
  }, [maintenance]);

  useEffect(() => {
    setDismissedChamberInfo(false);
  }, [chamberMessage.message]);

  useEffect(() => {
    setInfoMessage({
      ...infoMessage,
      message: globalMessageData?.data?.globalMessage,
    });
    const delay = maintenanceChanged.changed ? 20 * 1000 : 10 * 1000;

    if (
      infoMessage.message &&
      infoMessage.message !== info &&
      !isLoadingGlobalMessage &&
      !dismissedInfo
    ) {
      setShowInfo(true);
      setInfo(infoMessage.message);
    } else {
      setShowInfo(false);
      setInfo("");
    }

    const timer = setTimeout(() => {
      setShowInfo(false);
    }, delay);

    return () => clearTimeout(timer);
  }, [infoMessage.message, isLoadingGlobalMessage, dismissedInfo]);

  useEffect(() => {
    if (
      !dismissedMaintenance &&
      maintenance &&
      maintenance !== maintenanceChanged.message
    ) {
      setMaintenanceChanged({ changed: true, message: maintenance });
    } else {
      setMaintenanceChanged((prev) => ({ ...prev, changed: false }));
      setShowInfo(false);
    }

    const timer = setTimeout(() => {
      setMaintenanceChanged((prev) => ({ ...prev, changed: false }));
    }, 10 * 1000);

    return () => clearTimeout(timer);
  }, [maintenance, dismissedMaintenance]);

  useEffect(() => {
    setChamberMessage({
      ...chamberMessage,
      message: chamberMessageData?.data?.chamberInfoMessage,
    });
    const delay =
      maintenanceChanged.changed && showInfo
        ? 30 * 1000
        : maintenanceChanged.changed || showInfo
          ? 20 * 1000
          : 10 * 1000;

    if (
      chamberMessage.message &&
      chamberMessage.message !== chamberInfo &&
      !isLoadingChamberGlobalMessage &&
      !dismissedChamberInfo
    ) {
      setShowChamberInfo(true);
      setChamberInfo(chamberMessage.message);
    }

    const timer = setTimeout(() => {
      setShowChamberInfo(false);
    }, delay);

    return () => clearTimeout(timer);
  }, [
    chamberMessage.message,
    showInfo,
    isLoadingChamberGlobalMessage,
    dismissedChamberInfo,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {maintenanceChanged.changed && maintenance && !dismissedMaintenance && (
          <Box
            sx={{
              background: "#f8d7da",
              position: "fixed",
              zIndex: 1000,
              p: 1,
              width: "fit-content",
              borderRadius: ".25rem",
              minHeight: "3rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
              animation: `${slideInOutAnimation} 10s ease-in-out forwards`,
            }}
          >
            <Typography>
              {t(
                "Έχουν προγραμματιστεί εργασίες συντήρησης στο σύστημα για τις",
              )}{" "}
              {maintenance}
            </Typography>
            <IconButton
              size="small"
              aria-label="Close message"
              onClick={handleDismissMaintenance}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        {info &&
          showInfo &&
          !isLoadingGlobalMessage &&
          !maintenanceChanged.changed && (
            <Box
              sx={{
                background: "#D9EDF7",
                position: "fixed",
                zIndex: 1000,
                p: 1,
                pr: 5,
                width: "fit-content",
                borderRadius: ".25rem",
                minHeight: "3rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
                animation: `${slideInOutAnimation} 10s ease-in-out forwards`,
              }}
            >
              <Box
                sx={{
                  width: "95%",
                  display: "flex",
                  alignItems: "center",
                  mx: "auto",
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: info }} />
              </Box>
              <IconButton
                size="small"
                aria-label="Close message"
                onClick={handleDismissInfo}
                sx={{ position: "absolute", top: 4, right: 4 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        {!isLoadingChamberGlobalMessage &&
          showChamberInfo &&
          !maintenanceChanged.changed &&
          !showInfo &&
          isChamber && (
            <Box
              sx={{
                background: "#FED7A0",
                position: "fixed",
                zIndex: 1000,
                p: 1,
                pr: 5,
                width: "fit-content",
                borderRadius: ".25rem",
                minHeight: "3rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                animation: `${slideInOutAnimation} 10s ease-in-out forwards`,
              }}
            >
              <Box
                sx={{
                  width: "95%",
                  display: "flex",
                  alignItems: "center",
                  mx: "auto",
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: chamberInfo }} />
              </Box>
              <IconButton
                size="small"
                aria-label="Close message"
                onClick={handleDismissChamberInfo}
                sx={{ position: "absolute", top: 4, right: 4 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
      </Box>
      {children}
    </>
  );
}
